import { AxiosError } from "axios";

import type QueryClient from "../api/query/queryClient";

export function claimKeyFromProcedureKey(procedureKey: string): string | null {
  const wieldyIdParts = procedureKey.split(".");
  switch (wieldyIdParts.length) {
    case 2:
      return wieldyIdParts[0];
    case 4:
      return wieldyIdParts.slice(0, 3).join(".");
    default:
      return null;
  }
}

export async function deleteClaimAndNotify(
  claim: ClaimWithProcedureAndPatientMessage,
  queryClient: QueryClient,
  updateStore: (action: { type: "DELETE_CLAIM"; claimKey: string }) => void,
  updateSnackBar: (snackbar: {
    severity: "success" | "error" | "info";
    message: string;
  }) => void,
) {
  try {
    updateStore({
      type: "DELETE_CLAIM",
      claimKey: claim.wieldyId,
    });
    const claimKey = await queryClient.deleteClaim(claim);
    updateSnackBar({
      severity: "success",
      message: `Successfully deleted claim: ${claimKey}`,
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          (typeof responseBody.detail === "string"
            ? responseBody.detail
            : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
      });
    } else {
      updateSnackBar({
        severity: "error",
        message: "An unknown error occurred.",
      });
    }
  }
}

export async function deleteProcedureAndNotify(
  procedure: ProcedureMessage,
  claims: ClaimWithProcedureAndPatientMessage[],
  queryClient: QueryClient,
  updateStore: (action: {
    type: "DELETE_PROCEDURE";
    claimKey: string;
    procedureKey: string;
  }) => void,
  updateSnackBar: (snackbar: {
    severity: "success" | "error" | "info";
    message: string;
  }) => void,
) {
  const claimKey = claimKeyFromProcedureKey(procedure.wieldyId);
  if (!claimKey) {
    updateSnackBar({
      severity: "error",
      message: `Invalid Wieldy ID for procedure: ${procedure.wieldyId}`,
    });
    return;
  }
  const existingClaim = claims.find((c) => c.wieldyId === claimKey);
  if (!existingClaim) {
    updateSnackBar({
      severity: "error",
      message: `Claim not found for procedure: ${claimKey}`,
    });
    return;
  }
  try {
    updateStore({
      type: "DELETE_PROCEDURE",
      claimKey,
      procedureKey: procedure.wieldyId,
    });
    const procedureKey = await queryClient.deleteProcedure(
      procedure,
      existingClaim.practiceId,
    );
    updateSnackBar({
      severity: "success",
      message: `Successfully deleted procedure: ${procedureKey}`,
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          (typeof responseBody.detail === "string"
            ? responseBody.detail
            : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
      });
    } else {
      updateSnackBar({
        severity: "error",
        message: "An unknown error occurred.",
      });
    }
  }
}

export async function saveProcedureAndNotify(
  procedure: ProcedureMessage,
  claims: ClaimWithProcedureAndPatientMessage[],
  queryClient: QueryClient,
  updateStore: (action: {
    type: "CREATE_PROCEDURE" | "UPDATE_PROCEDURE";
    claimKey: string;
    procedure: ProcedureMessage;
  }) => void,
  updateSnackBar: (snackbar: {
    severity: "success" | "warning" | "error" | "info";
    message: string;
  }) => void,
) {
  if (procedure.wieldyId) {
    const claimKey = claimKeyFromProcedureKey(procedure.wieldyId);
    if (!claimKey) {
      updateSnackBar({
        severity: "error",
        message: `Invalid Wieldy ID for procedure: ${procedure.wieldyId}`,
      });
      return;
    }
    const existingClaim = claims.find((c) => c.wieldyId === claimKey);
    if (!existingClaim) {
      updateSnackBar({
        severity: "error",
        message: `Claim not found for procedure: ${claimKey}`,
      });
      return;
    }
    const existingProcedure = existingClaim.Procedure.find(
      (p) => p.wieldyId === procedure.wieldyId,
    );
    if (existingProcedure) {
      const deltas = (
        Object.entries(procedure) as [keyof ProcedureMessage, unknown][]
      ).reduce<Partial<ProcedureMessage>>(
        (acc, [key, value]) => {
          // We only need practiceId to update the procedure
          if (key === "practiceId") {
            return acc;
          }
          if (value !== undefined && existingProcedure[key] !== value) {
            // biome-ignore lint/suspicious/noExplicitAny: assigning through a generic key the types can't narrow
            (acc[key] as any) = value;
          }
          return acc;
        },
        {} as Partial<ProcedureMessage>,
      );
      if (Object.keys(deltas).length === 0) {
        updateSnackBar({
          severity: "warning",
          message: `Nothing to update`,
        });
        return;
      }
      try {
        await queryClient.updateProcedure({
          ...deltas,
          wieldyId: procedure.wieldyId,
          practiceId: procedure.practiceId,
        });
        updateStore({
          type: "UPDATE_PROCEDURE",
          claimKey,
          procedure,
        });
        updateSnackBar({
          severity: "success",
          message: `Successfully edited procedure: ${procedure.wieldyId}`,
        });
        return;
      } catch (error: unknown) {
        if (error instanceof AxiosError && error.response?.data) {
          const responseBody = error.response.data;
          updateSnackBar({
            severity: "error",
            message:
              responseBody.detail?.[0]?.msg || "An unknown error occurred.",
          });
        } else {
          updateSnackBar({
            severity: "error",
            message: "An unknown error occurred.",
          });
        }
        return;
      }
    }
  }
  try {
    const procedureKey = await queryClient.createProcedure(procedure);
    const claimKey = claimKeyFromProcedureKey(procedureKey);
    if (!claimKey) {
      updateSnackBar({
        severity: "error",
        message: "Unable to create procedure",
      });
      return;
    }
    updateStore({
      type: "CREATE_PROCEDURE",
      claimKey,
      procedure,
    });
    updateSnackBar({
      severity: "success",
      message: `Successfully create procedure: ${procedureKey}`,
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          (typeof responseBody.detail === "string"
            ? responseBody.detail
            : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
      });
    } else {
      updateSnackBar({
        severity: "error",
        message: "An unknown error occurred.",
      });
    }
  }
}
